import { computed, observable, transaction, makeObservable } from "mobx"
import { RootStore } from "stores/root.store"
import { PaginationStore } from "stores/data/pagination.store"
import {
  completeGameTaskBuffPlay,
  getAvailableGamesBuffPlay,
  installGameBuffPlay,
  resetGameTasksBuffPlay,
} from "../api/transactions-BuffPlay.call"
import { SourceGameEnum, SourceGameType } from "../constants/source-options"

export const GameAttributionPlatform = {
  ADJUST: "ADJUST",
  APPS_FLYER: "APPS_FLYER",
  SINGULAR: "SINGULAR",
  EVERFLOW: "EVERFLOW",
} as const

export type GameAttributionPlatform = keyof typeof GameAttributionPlatform;

export const GameTaskType = {
  LEVEL_TASK: "LEVEL_TASK",
  BONUS_TASK: "BONUS_TASK",
  CASHBACK_TASK: "CASHBACK_TASK",
  INSTALL_TASK: "INSTALL_TASK",
  WATCH_AN_ADS_TASK: "WATCH_AN_ADS_TASK",
} as const

export type GameTaskType = keyof typeof GameTaskType;

export const GamePlatformType = { ANDROID: "ANDROID", IOS: "IOS" } as const

export type GamePlatformType = (typeof GamePlatformType)[keyof typeof GamePlatformType];


export type AccountTransactionsFilterBuffPlay = {
  lastDate?: string;
  name?: string;
  source?: string;
  limit?: number;
  gameId?: number[];
  createdAtOrder?: string;
};

export interface AccountNativeGameTypeBuffPlay {
  id: string
  name: string
  category: string
  bannerUrl: string
  description?: string | null
  earningSpeed: number
  isInstallable: boolean
  dailyBudget?: number
  monthlyBudget?: number
  isBudgetActive?: boolean
  isTopPick: boolean
  videoBannerUrl?: string | null
  isDeleted?: Date | null
  // Unified games schema (buff.services #839): native = NATIVE, aggregators carry their enum value.
  gameSource: SourceGameType
  gameOrigin?: SourceGameType
  platforms: GamePlatform[]
  tasks: GameTask[]
}

export interface GamePlatform {
  platform: GamePlatformType
  platformId: string
  iconUrl: string
  rating: number
  installRevenue?: number
  adjustLinkToken?: string
  ctaLink: string
  ctaCampaign: string
  attributionPlatform: GameAttributionPlatform
  gamePublisher?: GamePublisher
}

export interface GamePublisher {
  id: string
  name: string
  costReportingPlatform: GameAttributionPlatform
}

export interface GameTask {
  id: string
  name: string
  platform: GamePlatformType
  reward: number
  rewardUsd: number | null
  sequence: number
  type: GameTaskType
  completed?: boolean
  appsflyerRevenue: number | null
  cpeRevenue: number | null
  bonusReward: number | null
  expiresInMinutes: number | null
  iconNumber: number
  taskToken: string
  adsWatchedTotalRequired?: number | null
  status?: string
}

export type AccountGameTypeBuffPlay = AccountNativeGameTypeBuffPlay

export class AccountGamesBuffPlayStore extends PaginationStore<
  AccountGameTypeBuffPlay,
  AccountTransactionsFilterBuffPlay
> {
  @observable
  private _accountGames: AccountGameTypeBuffPlay[] = []
  @observable
  private _currentId: string
  // Server-side gameSource filter for the unified games list (NATIVE by default).
  @observable
  private _gameSource: SourceGameType = SourceGameEnum.NATIVE
  @observable
  private _name: string = ""

  @observable
  public loadedPages: number = 1
  @observable
  public isEmptyArr: boolean = false

  constructor(rootStore: RootStore) {
    super(rootStore)
    makeObservable(this)
    this.pagination.limit = 20
  }
  public async getData() {
    this.isEmptyArr = false

    if (this._currentId) {
      return this.getGamesBuffPlay(this._currentId)
    } else {
      return []
    }
  }

  public async getGamesBuffPlay(id: string) {
    this.resetFuncAfterSuccess()
    if (this._currentId !== id) {
      this._currentId = id
      this._accountGames = []
    }
    if (this._accountGames.length === 0) {
      this.loadedPages = 1
    }
    this.isEmptyArr = false

    const accountGames = await this.defaultFunction(
      getAvailableGamesBuffPlay,
      id,
      this.loadedPages,
      50,
      this._name,
      { source: this._gameSource },
    )
    if (accountGames) {
      transaction(() => {
        this.isLoading = false
        if (accountGames.data?.length === 0) {
          this.isEmptyArr = true
          this.loadedPages = 0
        }
        if (this.loadedPages > 1) {
          this._accountGames = [...this._accountGames, ...accountGames.data]
        } else {
          this._accountGames = accountGames.data
        }
        this.total = accountGames.total
      })
    }

    return accountGames.data
  }

  async completeGameTask(
    {
      accountId,
      gameId,
      taskId,
      platform,
      reward,
      rewardUsd,
      taskName,
      taskType,
      status,
    }:
    {
      accountId: string,
      gameId: string,
      taskId: string,
      platform?: string
      reward: number,
      rewardUsd?: number | null,
      taskName: string,
      taskType: string
      status?: string,
    }) {
    const response = await this.defaultFunction(
      completeGameTaskBuffPlay,
      accountId,
      gameId,
      taskId,
      platform || "",
      reward,
      taskName,
      taskType,
      rewardUsd,
      status,
    )

    if (!response.success) {
      throw new Error(response?.data?.payload || "Unknown error occurred")
    }
  }

  async installGameBuffPlay(
    {
      accountId,
      gameId,
      platform,
    }:
    {
      accountId: string,
      gameId: string,
      platform: GamePlatformType,
    }) {
    const response = await this.defaultFunction(installGameBuffPlay, accountId, gameId, platform)

    if (!response.success) {
      throw new Error(response?.data?.payload || "Unknown error occurred")
    }
  }

  async resetGameTasksBuffPlay(
    {
      accountId,
      gameId,
      platform,
    }:
    {
      accountId: string,
      gameId: string,
      platform: GamePlatformType,
    }) {
    await this.defaultFunction(resetGameTasksBuffPlay, accountId, gameId, platform)
  }

  @computed
  get accountGames(): AccountGameTypeBuffPlay[] {
    return this._accountGames
  }

  set accountGames(value: AccountGameTypeBuffPlay[]) {
    this._accountGames = value
  }

  @computed
  get selectedGames(): AccountGameTypeBuffPlay[] {
    return this._accountGames
  }

  @computed
  get gameSource(): SourceGameType {
    return this._gameSource
  }

  set gameSource(value: SourceGameType) {
    this._gameSource = value
  }

  @computed
  get gameName(): string {
    return this._name
  }

  set gameName(value: string) {
    this._name = value
  }
}
